import _extends from "@babel/runtime/helpers/extends";
import _classCallCheck from "@babel/runtime/helpers/classCallCheck";
import _createClass from "@babel/runtime/helpers/createClass";
import _possibleConstructorReturn from "@babel/runtime/helpers/possibleConstructorReturn";
import _getPrototypeOf from "@babel/runtime/helpers/getPrototypeOf";
import _inherits from "@babel/runtime/helpers/inherits";
function _callSuper(t, o, e) { return o = _getPrototypeOf(o), _possibleConstructorReturn(t, _isNativeReflectConstruct() ? Reflect.construct(o, e || [], _getPrototypeOf(t).constructor) : o.apply(t, e)); }
function _isNativeReflectConstruct() { try { var t = !Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function () {})); } catch (t) {} return (_isNativeReflectConstruct = function _isNativeReflectConstruct() { return !!t; })(); }
import PropTypes from 'prop-types';
import React from 'react';
import classNames from 'classnames';
import Text from '../text/text';
import TextButton from '../textButton/textButton';
import InfoIcon from '../infoIcon/infoIcon';
import { dataHooks } from './constants';
import compose from '../../hoc/compose';
import displayNames from '../displayNames';
var NOTIFICATION_BANNER_TYPES = {
  INLINE: 'inline',
  FLOATING: 'floating'
};
var NOTIFICATION_BANNER_ALIGNMENT = {
  CENTER: 'center',
  START: 'start'
};
var NOTIFICATION_BANNER_SKINS = {
  STANDARD: 'standard',
  PREMIUM: 'premium',
  ALERT: 'alert',
  SUCCESS: 'success',
  WARNING: 'warning',
  NEUTRAL: 'neutral'
};
var TEXT_BUTTON_SKINS = {
  STANDARD: 'standard',
  PREMIUM: 'premium',
  DARK: 'dark'
};
var NOTIFICATION_BANNER_ICON_TYPES = {
  INFO: 'info',
  ALERT: 'alert'
};
var NotificationBanner = /*#__PURE__*/function (_React$PureComponent) {
  function NotificationBanner() {
    _classCallCheck(this, NotificationBanner);
    return _callSuper(this, NotificationBanner, arguments);
  }
  _inherits(NotificationBanner, _React$PureComponent);
  return _createClass(NotificationBanner, [{
    key: "render",
    value: function render() {
      var _this$props = this.props,
        alignment = _this$props.alignment,
        getClassName = _this$props.getClassName,
        dataHook = _this$props.dataHook,
        skin = _this$props.skin,
        text = _this$props.text,
        link = _this$props.link,
        icon = _this$props.icon,
        multiline = _this$props.multiline,
        type = _this$props.type,
        shouldTranslate = _this$props.shouldTranslate;
      var getComputedAlignment = function getComputedAlignment() {
        if (alignment) {
          return alignment;
        }
        return type === NOTIFICATION_BANNER_TYPES.FLOATING ? NOTIFICATION_BANNER_ALIGNMENT.START : NOTIFICATION_BANNER_ALIGNMENT.CENTER;
      };
      var rootClassName = classNames(getClassName('notification-banner'), "notification-banner-skin-".concat(skin), "notification-banner-type-".concat(type), "notification-banner-alignment-".concat(getComputedAlignment()));
      var getLinkButton = function getLinkButton() {
        var dynamicProps = {
          dataHook: dataHooks.notificationLink,
          skin: Object.values(TEXT_BUTTON_SKINS).includes(skin) ? skin : undefined,
          shouldTranslate: shouldTranslate
        };
        if (link.onClick) {
          dynamicProps.onClick = link.onClick;
        } else {
          dynamicProps.as = 'a';
          dynamicProps.href = link.href;
          dynamicProps.target = link.target;
        }
        return /*#__PURE__*/React.createElement(TextButton, _extends({
          size: "small",
          weight: "thin"
        }, dynamicProps), link.label);
      };

      /**
       * Root element is a horizontal flex container.
       * Main content is nested in it (inflexible) and has a flexible side element on each side.
       * Each side element grows and consumes as much free space as possible, which forces the main content to sit exactly in the center of the root element.
       * This allows the icon to always sit at the very end of the root element, without affecting the position of the main content.
       * To align the main content to the start of the root element (when needed), we simply turn the start side element inflexible.
       */
      return /*#__PURE__*/React.createElement("div", {
        "data-hook": dataHook,
        className: rootClassName
      }, /*#__PURE__*/React.createElement("div", {
        className: "notification-banner-side-start"
      }), /*#__PURE__*/React.createElement("div", {
        className: "notification-banner-contents"
      }, /*#__PURE__*/React.createElement("span", {
        className: "notification-banner-text"
      }, /*#__PURE__*/React.createElement(Text, {
        dataHook: dataHooks.notificationText,
        skin: "secondary",
        size: "small",
        weight: "thin",
        shouldTranslate: shouldTranslate,
        enableEllipsis: !multiline
      }, text, link && multiline && /*#__PURE__*/React.createElement(React.Fragment, null, ' ', /*#__PURE__*/React.createElement("span", {
        className: "notification-banner-link"
      }, getLinkButton())))), link && !multiline && /*#__PURE__*/React.createElement("div", {
        className: "notification-banner-link"
      }, getLinkButton())), /*#__PURE__*/React.createElement("div", {
        className: "notification-banner-side-end notification-banner-icon-wrapper"
      }, icon && /*#__PURE__*/React.createElement(InfoIcon, {
        dataHook: dataHooks.notificationBannerIcon,
        text: icon.tooltipText,
        className: "notification-banner-icon",
        error: icon.type === NOTIFICATION_BANNER_ICON_TYPES.ALERT,
        shouldTranslate: shouldTranslate
      })));
    }
  }]);
}(React.PureComponent);
NotificationBanner.displayName = displayNames.NOTIFICATION_BANNER;
NotificationBanner.propTypes = {
  /** A css class to be applied to the component's root element */
  className: PropTypes.string,
  /** Applies a data-hook HTML attribute that can be used in the tests */
  dataHook: PropTypes.string,
  /** Notification label */
  text: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  /** Changes visual theme */
  skin: PropTypes.oneOf(Object.values(NOTIFICATION_BANNER_SKINS)),
  /** Make text up to 2 lines if needed */
  multiline: PropTypes.bool,
  /** Sets the position of the text inside the banner. When omitted, it defaults to `center` for `inline` type and `start` for `floating` type */
  alignment: PropTypes.oneOf(Object.values(NOTIFICATION_BANNER_ALIGNMENT)),
  /** Display an info icon */
  icon: PropTypes.shape({
    type: PropTypes.oneOf(Object.values(NOTIFICATION_BANNER_ICON_TYPES)),
    tooltipText: PropTypes.string
  }),
  /** Link */
  link: PropTypes.shape({
    label: PropTypes.string.isRequired,
    onClick: PropTypes.func,
    href: PropTypes.string,
    target: PropTypes.string
  }),
  /** Changes notification type */
  type: PropTypes.oneOf(Object.values(NOTIFICATION_BANNER_TYPES)),
  /** Should translate the text */
  shouldTranslate: PropTypes.bool
};
NotificationBanner.defaultProps = {
  skin: NOTIFICATION_BANNER_SKINS.STANDARD,
  type: NOTIFICATION_BANNER_TYPES.INLINE,
  multiline: false
};
export default compose(NotificationBanner);